import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, UserPlus, UserMinus, Swords, Flame, Calendar, Share2, Copy, Check, X, ArrowLeft, MapPin, Clock, Ruler, Newspaper, Heart, MessageSquare, Send, Trash2 } from 'lucide-react';
import HamburgerMenu from '../components/HamburgerMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Friends = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [invites, setInvites] = useState([]);
  const [feed, setFeed] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({}); // postId -> draft text
  const [openReplies, setOpenReplies] = useState({}); // postId -> bool
  const [busyReact, setBusyReact] = useState({}); // postId-emoji -> bool

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        navigate('/');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const [friendsRes, requestsRes, invitesRes, feedRes] = await Promise.allSettled([
        axios.get(`${API}/friends`),
        axios.get(`${API}/friends/requests`),
        axios.get(`${API}/invites`),
        axios.get(`${API}/feed/friends?limit=20`)
      ]);

      if (friendsRes.status === 'fulfilled') {
        setFriends(friendsRes.value.data.friends || []);
      }
      if (requestsRes.status === 'fulfilled') {
        setFriendRequests(requestsRes.value.data.friend_requests || []);
      }
      if (invitesRes.status === 'fulfilled') {
        setInvites(invitesRes.value.data.invites || []);
      }
      if (feedRes.status === 'fulfilled') {
        setFeed(feedRes.value.data.posts || []);
      }

      // Identify the current user so we can mark "my reactions" / "my replies"
      try {
        const me = JSON.parse(localStorage.getItem('user') || '{}');
        if (me && me.id) setCurrentUserId(me.id);
      } catch (e) { /* noop */ }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading friends data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) return;
      
      const [friendsRes, requestsRes, invitesRes] = await Promise.allSettled([
        axios.get(`${API}/friends`),
        axios.get(`${API}/friends/requests`),
        axios.get(`${API}/invites`)
      ]);

      if (friendsRes.status === 'fulfilled') {
        setFriends(friendsRes.value.data.friends || []);
      }
      if (requestsRes.status === 'fulfilled') {
        setFriendRequests(requestsRes.value.data.friend_requests || []);
      }
      if (invitesRes.status === 'fulfilled') {
        setInvites(invitesRes.value.data.invites || []);
      }
    } catch (error) {
      console.error('Error refreshing friends data:', error);
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/accept`);
      toast.success('Friend request accepted!');
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    }
  };

  const declineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/decline`);
      toast.success('Friend request declined');
      refreshData();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const REACTIONS = [
    { key: 'fire', icon: '🔥', label: 'Fire' },
    { key: 'strong', icon: '💪', label: 'Strong' },
    { key: 'respect', icon: '❤️', label: 'Respect' },
  ];

  const toggleReaction = async (postId, emoji) => {
    const busyKey = `${postId}-${emoji}`;
    if (busyReact[busyKey]) return;
    setBusyReact((b) => ({ ...b, [busyKey]: true }));
    // optimistic update
    setFeed((posts) => posts.map((p) => {
      if (p.id !== postId) return p;
      const reactions = { ...(p.reactions || {}) };
      const arr = new Set(reactions[emoji] || []);
      if (currentUserId && arr.has(currentUserId)) {
        arr.delete(currentUserId);
      } else if (currentUserId) {
        arr.add(currentUserId);
      }
      reactions[emoji] = [...arr];
      return { ...p, reactions };
    }));
    try {
      await axios.post(`${API}/feed/${postId}/react`, { emoji });
    } catch (e) {
      toast.error('Could not react. Please try again.');
      // reload on failure
      try {
        const r = await axios.get(`${API}/feed/friends?limit=20`);
        setFeed(r.data.posts || []);
      } catch (_) { /* noop */ }
    } finally {
      setBusyReact((b) => {
        const next = { ...b };
        delete next[busyKey];
        return next;
      });
    }
  };

  const toggleReplies = (postId) => {
    setOpenReplies((o) => ({ ...o, [postId]: !o[postId] }));
  };

  const submitReply = async (postId) => {
    const draft = (replyDrafts[postId] || '').trim();
    if (!draft) return;
    try {
      const res = await axios.post(`${API}/feed/${postId}/reply`, { message: draft });
      const reply = res.data.reply;
      setFeed((posts) => posts.map((p) => p.id === postId
        ? { ...p, replies: [...(p.replies || []), reply] }
        : p
      ));
      setReplyDrafts((d) => ({ ...d, [postId]: '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply.');
    }
  };

  const deleteReply = async (postId, replyId) => {
    try {
      await axios.delete(`${API}/feed/${postId}/reply/${replyId}`);
      setFeed((posts) => posts.map((p) => p.id === postId
        ? { ...p, replies: (p.replies || []).filter((r) => r.id !== replyId) }
        : p
      ));
    } catch (e) {
      toast.error('Failed to delete reply.');
    }
  };

  const removeFriend = async (friendshipId, username) => {
    if (!window.confirm(`Remove ${username} from your friends?`)) return;
    
    try {
      await axios.delete(`${API}/friends/${friendshipId}`);
      toast.success('Friend removed');
      refreshData();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  const createInviteLink = async () => {
    try {
      const response = await axios.post(`${API}/invites/create`, null, {
        params: { max_uses: 10 }
      });
      toast.success('Invite link created!');
      setInvites([response.data, ...invites]);
    } catch (error) {
      toast.error('Failed to create invite link');
    }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(link);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const challengeFriend = (username) => {
    // Navigate to battle arena with the friend's username pre-filled
    navigate('/battle-arena', { state: { challengeUsername: username } });
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return then.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl">Loading Friends...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950 text-white pb-20">
      <HamburgerMenu />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-blood-400 mb-2 tracking-wider flex items-center justify-center gap-2">
            <Users className="w-8 h-8" />
            FRIENDS
          </h1>
          <p className="text-stone-400 text-sm">Your Squad</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-center mb-6">
          <Button
            onClick={() => navigate('/find-people')}
            className="bg-blood-700 hover:bg-blood-800"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Find People
          </Button>
          <Button
            onClick={() => setShowInviteModal(true)}
            variant="outline"
            className="border-blood-700 text-blood-400"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Invite
          </Button>
        </div>

        {/* Friends Feed */}
        {feed.length > 0 && (
          <Card className="glass border-emerald-800 p-4 mb-4" data-testid="friends-feed-card">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2 tracking-wider">
              <Newspaper className="w-5 h-5" />
              SQUAD FEED
            </h2>
            <div className="space-y-3">
              {feed.map((post) => {
                const reactions = post.reactions || {};
                const replies = post.replies || [];
                const repliesOpen = !!openReplies[post.id];
                return (
                <div key={post.id} className="border border-stone-800 rounded-lg p-3 bg-stone-950/40" data-testid={`feed-post-${post.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-emerald-900 border border-emerald-700 flex items-center justify-center text-xs font-bold text-emerald-300">
                        {(post.username || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <span className="text-white font-semibold text-sm">{post.username}</span>
                      {post.kind === 'activity' && <MapPin className="w-3 h-3 text-emerald-400" />}
                      {post.kind === 'workout' && <Flame className="w-3 h-3 text-blood-400" />}
                    </div>
                    <span className="text-stone-500 text-xs">{timeAgo(post.created_at)}</span>
                  </div>
                  {post.message && (
                    <p className="text-stone-200 text-sm mb-1.5 italic">"{post.message}"</p>
                  )}
                  <p className="text-stone-400 text-xs flex flex-wrap gap-x-3 gap-y-1">
                    <span className="text-emerald-400 font-semibold">{post.auto_text}</span>
                  </p>

                  {/* Reactions row */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {REACTIONS.map((r) => {
                      const users = reactions[r.key] || [];
                      const mine = currentUserId && users.includes(currentUserId);
                      const count = users.length;
                      return (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => toggleReaction(post.id, r.key)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
                            mine
                              ? 'bg-emerald-900/40 border-emerald-600 text-white'
                              : 'bg-stone-900 border-stone-700 text-stone-400 hover:text-white hover:border-stone-500'
                          }`}
                          data-testid={`feed-react-${r.key}-${post.id}`}
                          aria-label={`React ${r.label}`}
                        >
                          <span className="text-sm leading-none">{r.icon}</span>
                          {count > 0 && <span className="font-semibold">{count}</span>}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => toggleReplies(post.id)}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs border bg-stone-900 border-stone-700 text-stone-400 hover:text-white hover:border-stone-500 ml-auto"
                      data-testid={`feed-toggle-replies-${post.id}`}
                    >
                      <MessageSquare className="w-3 h-3" />
                      {replies.length > 0 ? `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}` : 'Reply'}
                    </button>
                  </div>

                  {/* Replies thread */}
                  {repliesOpen && (
                    <div className="mt-3 pt-3 border-t border-stone-800 space-y-2">
                      {replies.map((r) => (
                        <div key={r.id} className="flex items-start gap-2" data-testid={`feed-reply-${r.id}`}>
                          <div className="w-6 h-6 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center text-[10px] font-bold text-stone-300 shrink-0 mt-0.5">
                            {(r.username || '?').slice(0, 1).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-stone-200 text-xs font-semibold">{r.username}</span>
                              <span className="text-stone-600 text-[10px]">{timeAgo(r.created_at)}</span>
                              {(r.user_id === currentUserId || post.user_id === currentUserId) && (
                                <button
                                  type="button"
                                  onClick={() => deleteReply(post.id, r.id)}
                                  className="ml-auto text-stone-600 hover:text-blood-500"
                                  data-testid={`feed-reply-delete-${r.id}`}
                                  aria-label="Delete reply"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            <p className="text-stone-300 text-sm break-words">{r.message}</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 pt-1">
                        <input
                          type="text"
                          value={replyDrafts[post.id] || ''}
                          onChange={(e) => setReplyDrafts((d) => ({ ...d, [post.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitReply(post.id); }}
                          placeholder="Write a reply…"
                          maxLength={500}
                          className="flex-1 bg-stone-900 border border-stone-700 text-white text-sm rounded-md px-3 py-1.5 focus:outline-none focus:border-emerald-600"
                          data-testid={`feed-reply-input-${post.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => submitReply(post.id)}
                          disabled={!(replyDrafts[post.id] || '').trim()}
                          className="px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          data-testid={`feed-reply-send-${post.id}`}
                        >
                          <Send className="w-3 h-3" />
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Friend Requests */}
        {friendRequests.length > 0 && (
          <Card className="glass border-amber-700 p-4 mb-4">
            <h2 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              FRIEND REQUESTS ({friendRequests.length})
            </h2>
            <div className="space-y-2">
              {friendRequests.map((request) => (
                <div
                  key={request.id}
                  className="glass border border-amber-700 rounded-lg p-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <div className="font-bold text-amber-400">
                        {request.sender_username}
                      </div>
                      <div className="text-xs text-stone-400">
                        {getTimeAgo(request.created_at)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => acceptRequest(request.id)}
                        className="bg-blood-700 hover:bg-blood-800"
                        size="sm"
                      >
                        Accept
                      </Button>
                      <Button
                        onClick={() => declineRequest(request.id)}
                        variant="outline"
                        className="border-stone-700 text-stone-400"
                        size="sm"
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Friends List */}
        <Card className="glass border-blood-700 p-4">
          <h2 className="text-lg font-bold text-blood-400 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            YOUR FRIENDS ({friends.length})
          </h2>
          
          {friends.length === 0 ? (
            <div className="text-center py-10 text-stone-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="mb-4 text-sm">No friends yet. Start building your training network!</p>
              <Button
                onClick={() => navigate('/find-people')}
                className="bg-blood-700 hover:bg-blood-800"
              >
                Find People
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {friends.map((friend) => (
                <div
                  key={friend.friendship_id}
                  className="glass border border-stone-700 rounded-lg p-3 hover:border-blood-700 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-blood-400">
                        {friend.username}
                      </div>
                      {friend.current_streak > 0 && (
                        <div className="text-xs text-stone-400 flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-500" />
                          {friend.current_streak} day streak
                        </div>
                      )}
                    </div>
                  </div>

                  {friend.last_workout && (
                    <div className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Last: {getTimeAgo(friend.last_workout)}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => challengeFriend(friend.username)}
                      className="flex-1 bg-blood-700 hover:bg-blood-800 text-xs"
                      size="sm"
                    >
                      <Swords className="w-3 h-3 mr-1" />
                      Challenge
                    </Button>
                    <Button
                      onClick={() => removeFriend(friend.friendship_id, friend.username)}
                      variant="outline"
                      className="border-stone-700 text-stone-400 text-xs px-2"
                      size="sm"
                    >
                      <UserMinus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Invite Modal - Fixed for mobile */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <Card className="glass border-blood-700 p-4 sm:p-6 w-full sm:max-w-md sm:rounded-lg rounded-t-2xl rounded-b-none sm:rounded-b-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blood-400 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                INVITE FRIENDS
              </h2>
              <button onClick={() => setShowInviteModal(false)} className="text-stone-400 hover:text-white p-1">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {invites.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-stone-400 mb-4 text-sm">Create an invite link to share with friends</p>
                <Button
                  onClick={createInviteLink}
                  className="bg-blood-700 hover:bg-blood-800"
                >
                  Create Invite Link
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {invites.slice(0, 3).map((invite) => (
                  <div key={invite.id} className="glass border border-stone-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-stone-400">
                        Code: <span className="text-blood-400 font-mono">{invite.invite_code}</span>
                      </div>
                      <div className="text-xs text-stone-500">
                        {invite.uses}/{invite.max_uses} uses
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={invite.invite_link}
                        readOnly
                        className="flex-1 glass border border-stone-800 text-white text-xs px-2 py-2 rounded"
                      />
                      <Button
                        onClick={() => copyInviteLink(invite.invite_link)}
                        className="bg-stone-700 hover:bg-stone-600 px-3"
                        size="sm"
                      >
                        {copiedLink === invite.invite_link ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
                
                <Button
                  onClick={createInviteLink}
                  variant="outline"
                  className="w-full border-stone-700 text-stone-400"
                >
                  Create Another Link
                </Button>
              </div>
            )}

            <Button
              onClick={() => setShowInviteModal(false)}
              variant="outline"
              className="w-full mt-4 border-stone-700 text-stone-400"
            >
              Close
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Friends;
